import { HandleRefreshRequest } from '@demand-iq/auth-sdk';
import { Token, coreUserAccessToken, coreUserRefreshToken, decodeToken } from 'hooks/useToken';
import AuthApi from './authClient';

/**
 * Renew a token this far ahead of its own expiry, so a request that is already in
 * flight cannot race the cliff. Core-api issues access tokens with a 20-minute life.
 */
export const TOKEN_RENEWAL_LEAD_MS = 3 * 60 * 1000;

/** Expiry of a core-api JWT, or null when it cannot be read. */
export const getTokenExpiry = (token: string | null): Date | null => {
  if (!token) {
    return null;
  }
  try {
    const { exp } = decodeToken(token).registeredClaims;
    if (!exp) {
      return null;
    }
    return new Date(exp * 1000);
  } catch {
    return null;
  }
};

/** True when the token is inside its renewal window, already expired, or unreadable. */
export const isTokenExpiring = (token: string | null, leadMs: number = TOKEN_RENEWAL_LEAD_MS): boolean => {
  const expiry = getTokenExpiry(token);
  if (!expiry) {
    return true;
  }
  return expiry.getTime() - Date.now() <= leadMs;
};

/**
 * Refreshes the user token with promise-deduplication.
 * Concurrent callers await the same in-flight promise so only one refresh request is made.
 */
let userRefreshInProgress: Promise<Token | null> | null = null;

export const refreshUserToken = async (refreshToken: string): Promise<Token | null> => {
  if (userRefreshInProgress) {
    return userRefreshInProgress;
  }

  userRefreshInProgress = (async () => {
    const body = new HandleRefreshRequest();
    body.token = refreshToken;
    const response = await AuthApi.handleRefresh({ handleRefreshRequest: body });
    if (response?.accessToken && response?.refreshToken) {
      return { accessToken: response.accessToken, refreshToken: response.refreshToken };
    }
    return null;
  })();

  try {
    return await userRefreshInProgress;
  } finally {
    userRefreshInProgress = null;
  }
};

/**
 * Returns a usable access token for the logged-in user, refreshing it first when it is
 * at or past expiry.
 *
 * Spoofing depends on this: a fresh spoof token can only be minted with the admin's own
 * token, and that token runs on the same 20-minute clock as the spoof token it renews.
 */
export const ensureFreshUserToken = async (): Promise<string | null> => {
  const accessTokenKey = coreUserAccessToken();
  const accessToken = localStorage.getItem(accessTokenKey);
  if (accessToken && !isTokenExpiring(accessToken)) {
    return accessToken;
  }

  const refreshTokenKey = coreUserRefreshToken();
  const refreshToken = localStorage.getItem(refreshTokenKey);
  if (!refreshToken) {
    return accessToken;
  }

  try {
    const refreshed = await refreshUserToken(refreshToken);
    if (!refreshed?.accessToken) {
      return null;
    }
    localStorage.setItem(accessTokenKey, refreshed.accessToken);
    if (refreshed.refreshToken) {
      localStorage.setItem(refreshTokenKey, refreshed.refreshToken);
    }
    return refreshed.accessToken;
  } catch {
    return null;
  }
};
